import React from "react";
import {
  View,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { Play, Pause } from "lucide-react";
import NoteHeader from "./NoteHeader";
import NoteActions from "./NoteActions";
import NoteSummary from "./NoteSummary";
import { VoiceNoteCardProps } from "../../types/VoiceNotes";

const VoiceNoteCard: React.FC<VoiceNoteCardProps> = ({
  note,
  style,
  playingNoteId,
  loadingAudio,
  editingNoteId,
  editingField,
  editedSummary,
  expandedSummaries,
  signedNotes,
  saving,
  tipsEnabled,
  tipsState,
  getNoteTypeLabel,
  onPlayPause,
  onDownloadSummary,
  onDownloadSignedPDF,
  onSignDocument,
  onDeleteNote,
  onEditSummary,
  onSaveSummary,
  onCancelEdit,
  onToggleSummary,
  onDownloadTranscript,
  onDownloadAudio,
  onReprocessNote,
  reprocessingNoteId,
  onMarkStepComplete,
  setEditedSummary,
  TOOLTIP_STEPS,
}) => {
  return (
    <View style={[styles.noteCard, style]}>
      <NoteHeader note={note} getNoteTypeLabel={getNoteTypeLabel} />

      <View style={styles.actionRow}>
        <TouchableOpacity
          style={[
            styles.playButton,
            loadingAudio && playingNoteId === note.id
              ? styles.loadingButton
              : null,
          ]}
          onPress={() => onPlayPause(note)}
          disabled={loadingAudio}
        >
          {loadingAudio && playingNoteId === note.id ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : playingNoteId === note.id ? (
            <Pause size={20} color="#FFFFFF" />
          ) : (
            <Play size={20} color="#FFFFFF" />
          )}
        </TouchableOpacity>

        <NoteActions
          note={note}
          editingNoteId={editingNoteId}
          signedNotes={signedNotes}
          reprocessingNoteId={reprocessingNoteId}
          onDownloadSummary={onDownloadSummary}
          onDownloadSignedPDF={onDownloadSignedPDF}
          onSignDocument={onSignDocument}
          onDeleteNote={onDeleteNote}
          onEditSummary={onEditSummary}
          onDownloadTranscript={onDownloadTranscript}
          onDownloadAudio={onDownloadAudio}
          onReprocessNote={onReprocessNote}
        />
      </View>

      <NoteSummary
        note={note}
        editingNoteId={editingNoteId}
        editingField={editingField}
        editedSummary={editedSummary}
        expandedSummaries={expandedSummaries}
        saving={saving}
        tipsEnabled={tipsEnabled}
        tipsState={tipsState}
        onSaveSummary={onSaveSummary}
        onCancelEdit={onCancelEdit}
        onToggleSummary={onToggleSummary}
        onMarkStepComplete={onMarkStepComplete}
        setEditedSummary={setEditedSummary}
        TOOLTIP_STEPS={TOOLTIP_STEPS}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  noteCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    borderWidth: 2,
    borderColor: "#EEEEEE",
    shadowColor: "#111827",
    shadowOpacity: 0.08,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
    alignSelf: "flex-start",
  },
  actionRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  playButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#0085FF",
    justifyContent: "center",
    alignItems: "center",
  },
  loadingButton: {
    opacity: 0.7,
  },
});

export default VoiceNoteCard;
